// Third party imports
import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import Autosuggest from 'react-autosuggest';
import debounce from 'lodash/debounce';
// Checkout imports
import { ADDRESS_LOOKUP_THEME } from './constants';
import { JBButton } from '@jbhi-fi/lanyard-ui';
import Alert from '@jbhi-fi/lanyard-ui/components/Alert';
import { getAddressValidationClient } from '../../../api/qas/getAddressValidationClient';

export default class AddressLookup extends Component {
  constructor(props) {
    super(props);

    this.state = {
      query: props.initialOption ? props.initialOption : props.initialQuery,
      fetchingResults: false,
      results: [],
      message: '',
      errorMessage: '',
    };

    // Handlers
    this.handleClearLookup = this.handleClearLookup.bind(this);
    this.handleQueryChange = this.handleQueryChange.bind(this);
    this.handleInputBlur = this.handleInputBlur.bind(this);
    this.handleManualButtonClick = this.handleManualButtonClick.bind(this);

    // Autosuggest Methods
    this.onSuggestionsFetchRequested = debounce(this.onSuggestionsFetchRequested.bind(this), 200);
    this.onSuggestionSelected = this.onSuggestionSelected.bind(this);
    this.getSuggestionValue = this.getSuggestionValue.bind(this);
    this.renderSuggestion = this.renderSuggestion.bind(this);
    this.renderSuggestionsContainer = this.renderSuggestionsContainer.bind(this);

    // Refs
    this.storeInputReference = this.storeInputReference.bind(this);
    this.searchInput = null;
  }

  handleClearLookup() {
    this.setState({
      query: '',
      results: [],
      resultCount: 0,
      message: '',
      errorMessage: '',
    });

    // Callback provided by prop
    this.props.handleCleared();

    // Focus input on clear
    if (this.searchInput) {
      setTimeout(() => {
        this.searchInput.focus();
      }, 0);
    }
  }

  handleQueryChange(event, { newValue, method }) {
    if (newValue && (method === 'type' || method === 'click')) {
      this.setState({
        query: newValue,
      });
    } else if (newValue === '') {
      this.handleClearLookup();
    }
  }

  handleInputBlur(event) {
    if (this.props.handleInputBlured && typeof this.props.handleInputBlured === 'function') {
      this.props.handleInputBlured(event);
    }
  }

  handleManualButtonClick() {
    // Focus input on clear
    if (this.searchInput) {
      setTimeout(() => {
        this.searchInput.focus();
      }, 0);
    }

    this.props.handleManualClick();
    this.handleClearLookup();
  }

  onSuggestionsFetchRequested({ value, reason }) {
    if (reason === 'input-changed' || reason === 'input-focused') {
      const { maxResults, minQueryLength } = this.props;

      if (value.trim().length > minQueryLength) {
        this.setState({
          fetchingResults: true,
        });

        const addressValidationClient = getAddressValidationClient();
        const searchAddress = this.props.handleInputChanged
          ? this.props.handleInputChanged
          : addressValidationClient.getQasSearch;

        searchAddress(value).then(({ data, error, statusCode }) => {
          if (error) {
            if (statusCode === 404) {
              this.setState({
                results: [],
                resultCount: 0,
                fetchingResults: false,
                message: 'No address found. Please try again or enter address manually.',
                errorMessage: '',
              });
            } else {
              console.error('addressLookup', error.message);

              window.dataLayer.push({
                event: 'sendEvent',
                eventCategory: 'Errors',
                eventAction: 'delivery',
                eventLabel: 'Error with address lookup',
              });

              this.setState({
                results: [],
                resultCount: 0,
                fetchingResults: false,
                message: '',
                errorMessage: 'Oops! An error occurred.',
              });

              window.appInsights?.trackEvent({
                name: 'Oops Error',
                properties: {
                  source: 'AddressLookup',
                  errorMessage: error.message,
                },
              });
            }

            return;
          }
          this.setState({
            results: data.slice(0, maxResults),
            resultCount: data.length,
            fetchingResults: false,
            message: error?.message,
            errorMessage: '',
          });

          return true;
        });
      }
    } else if (reason === 'escape-pressed') {
      this.handleClearLookup();
    }
  }

  onSuggestionSelected(event, { suggestion, suggestionValue, method }) {
    if (method === 'enter') {
      event.preventDefault();
    }

    // Callback provided by prop
    this.props.handleSuggestionSelected(suggestion, suggestionValue, this.state.query);

    this.setState({
      query: suggestionValue,
      results: [],
      resultCount: 0,
    });
  }

  getSuggestionValue(suggestion) {
    return this.props.formatSuggestion(suggestion);
  }

  renderSuggestion(suggestion) {
    return (
      <div>
        {this.props.showIcon ? (
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 10.63 16" className="icon">
            <use xlinkHref="#iconMapPin" />
            <title>Address marker</title>
          </svg>
        ) : null}
        <span>{this.props.formatSuggestion(suggestion)}</span>
      </div>
    );
  }

  renderSuggestionsContainer({ containerProps, children }) {
    const { maxResults, handleManualClick, manualClickLabel } = this.props;
    const { message, results, resultCount } = this.state;

    return (
      <Fragment>
        <div data-testid="address-lookup-suggestions-container" {...containerProps}>
          {children}

          {message ? (
            <div className="address-lookup__result address-lookup__message">
              <div>{message}</div>
            </div>
          ) : null}

          {resultCount > maxResults && results.length ? (
            <div className="address-lookup__result address-lookup__message address-lookup__message-counting">
              <div>
                Showing <em>{maxResults}</em> of <em>{resultCount}</em> results.
              </div>
            </div>
          ) : null}

          {handleManualClick ? (
            <div className="address-lookup__button-container">
              <span>or </span>
              <JBButton type="link" onClick={this.handleManualButtonClick}>
                {manualClickLabel}
              </JBButton>
            </div>
          ) : null}
        </div>
      </Fragment>
    );
  }

  storeInputReference(autosuggest) {
    if (autosuggest !== null) {
      this.searchInput = autosuggest.input;
    }
  }

  validateAddress() {
    const { query } = this.state;
    return query?.trim().length > 0;
  }
  componentDidUpdate(prevProps) {
    const { initialOption, initialQuery } = this.props;
    if (initialOption && initialOption !== prevProps.initialOption) {
      this.setState({
        query: initialOption,
        results: [],
        resultCount: 0,
      });
    } else if (initialQuery && !initialOption && initialQuery !== this.state.query) {
      // Focus input
      if (this.searchInput) {
        setTimeout(() => {
          this.searchInput.focus();
        }, 0);
      }

      this.setState({
        query: initialQuery,
      });

      this.onSuggestionsFetchRequested({
        value: initialQuery,
        reason: 'input-changed',
      });
    } else if (!initialOption && !initialQuery && (prevProps.initialOption || prevProps.initialQuery)) {
      this.setState({
        query: '',
        results: [],
        resultCount: 0,
      });
    }
  }

  render() {
    const { inputPlaceholder, inputLabel } = this.props;
    const { query, results, fetchingResults, errorMessage } = this.state;

    const inputProps = {
      value: query,
      onChange: this.handleQueryChange,
      onBlur: this.handleInputBlur,
      type: 'search',
      placeholder: inputPlaceholder,
      ref: this.searchInput,
      required: true,
      autoComplete: 'shipping address-line1',
    };

    return (
      <div className="address-lookup" data-testid="address-lookup">
        <div className="fieldset">
          <div className={`field field--full ${query.length ? ' field--show-floating-label has-value' : ''}`}>
            <div className={`field__input-wrapper address-lookup__input${fetchingResults ? ' is-fetching' : ''}`}>
              {inputLabel ? (
                <label className="field__label field__label--visible" htmlFor="cc_pickup_first_name">
                  {inputLabel}
                </label>
              ) : null}
              <Autosuggest
                suggestions={results}
                onSuggestionsFetchRequested={this.onSuggestionsFetchRequested}
                onSuggestionSelected={this.onSuggestionSelected}
                getSuggestionValue={this.getSuggestionValue}
                renderSuggestion={this.renderSuggestion}
                renderSuggestionsContainer={this.renderSuggestionsContainer}
                highlightFirstSuggestion={true}
                inputProps={inputProps}
                theme={ADDRESS_LOOKUP_THEME}
                ref={this.storeInputReference}
                focusInputOnSuggestionClick={false}
                alwaysRenderSuggestions={true}
              />
              {query.trim().length ? (
                <button type="button" className="address-lookup__clear" onClick={this.handleClearLookup}>
                  Clear
                </button>
              ) : null}
            </div>
          </div>
        </div>
        {errorMessage ? <Alert showIcon type="error" content={errorMessage} /> : null}
      </div>
    );
  }
}

AddressLookup.defaultProps = {
  initialQuery: '',
  initialOption: '',
  handleInputChanged: undefined,
  showIcon: true,
  inputPlaceholder: window.theme.strings.deliveryInputPlaceholder,
  inputLabel: window.theme.strings.deliveryInputLabel,
  manualClickLabel: 'Manual address',
  minQueryLength: 0,
  maxResults: 10,
  handleInputBlured: undefined,
};

AddressLookup.propTypes = {
  initialQuery: PropTypes.string,
  initialOption: PropTypes.string,
  handleInputChanged: PropTypes.func,
  handleSuggestionSelected: PropTypes.func.isRequired,
  handleCleared: PropTypes.func.isRequired,
  handleInputBlured: PropTypes.func,
  showIcon: PropTypes.bool,
  formatSuggestion: PropTypes.func.isRequired,
  handleManualClick: PropTypes.func,
  inputPlaceholder: PropTypes.string,
  inputLabel: PropTypes.string,
  manualClickLabel: PropTypes.string,
  minQueryLength: PropTypes.number,
  maxResults: PropTypes.number,
};
